import { useEffect } from 'react';

import { isPhoneDevice } from '@/lib/device';

/**
 * Keep a phone's screen on while a call rings or is live (Screen Wake Lock:
 * Android Chrome, and iOS 18.4+ for Home Screen apps). If the screen locks
 * mid-call, the app goes to the background - the ringtone stops and, on an
 * iPhone, the call audio can drop with it.
 *
 * Only while `active`; released the moment the call is over. The browser
 * drops the lock whenever the app is hidden, so it is taken again when the
 * app comes back. Laptops are left alone, and so is any browser without the
 * API - it is a nicety, never a requirement.
 */
export const useCallWakeLock = (active: boolean): void => {
  useEffect(() => {
    if (!active || !isPhoneDevice()) return;
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    const wakeLock = navigator.wakeLock;

    let sentinel: WakeLockSentinel | null = null;
    let pending = false;
    let done = false;

    const acquire = async (): Promise<void> => {
      if (done || pending || document.visibilityState !== 'visible') return;
      if (sentinel && !sentinel.released) return;
      pending = true; // one request at a time - never two locks, one leaked
      try {
        const next = await wakeLock.request('screen');
        if (done) {
          void next.release().catch(() => undefined);
          return;
        }
        sentinel = next;
      } catch {
        // Battery saver, an iframe, or the browser said no - the call goes on.
      } finally {
        pending = false;
      }
    };

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      done = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (sentinel && !sentinel.released) void sentinel.release().catch(() => undefined);
      sentinel = null;
    };
  }, [active]);
};
